// src/constants/bookingConst.js
//
// Shared, app-wide values for the Point of Sale build. This is the leaf every
// other module depends on, so it stays import-free.
//
// Ported from the court-booking system's bookingConst.js, trimmed to the
// constants the POS actually reads: the booking vocabulary (time slots, rate
// bands, court shapes) has no meaning here and was left behind deliberately
// rather than carried over unused. Per the project guidelines, any value that
// ends up shared across POS screens belongs in this file.

/**
 * The build's version number, as shown on the right of the landing page's
 * footer (components/PosLandingFooter.jsx).
 *
 * Sourced from REACT_APP_VERSION_NO in .env.common (falling back to .env —
 * see scripts/envFiles.js). Create React App inlines REACT_APP_* values at
 * BUILD time, so changing the number needs a rebuild or a dev-server restart.
 *
 * Normalised to exactly one leading "v" here rather than at the display site,
 * so "1.0.0" and "v1.0.0" both render "v1.0.0". Resolves to '' when the
 * variable is unset, and the footer then renders nothing rather than a bare
 * "v".
 */
export const APP_VERSION_NO = (() => {
  const raw = (process.env.REACT_APP_VERSION_NO || '').trim();
  if (!raw) return '';
  return /^v/i.test(raw) ? raw : `v${raw}`;
})();

/**
 * The product's own name, as shown in the browser tab and on the sign-in
 * screen.
 *
 * Set per deployment with REACT_APP_TAB_DISPLAY_NAME in .env.
 * public/index.html substitutes that variable directly so the tab is right
 * before any JavaScript runs; this constant is the fallback for when it is
 * unset, which is a real case and not a tidy one: Create React App leaves the
 * literal "%REACT_APP_TAB_DISPLAY_NAME%" sitting in the title bar rather than
 * blanking it. See src/index.js.
 *
 * Deliberately NOT REACT_APP_PROJECT_NAME: that variable names the Firebase
 * project this branch is allowed to deploy to, and check-env.js refuses the
 * deploy when it disagrees with the target. Free display text must never be
 * able to answer that question.
 *
 * Also NOT the club's name — that is config.courtName, comes from the
 * database, and differs per deployment of this same app.
 */
export const TAB_DISPLAY_NAME = process.env.REACT_APP_TAB_DISPLAY_NAME || 'WebsitePal POS';

export const BRAND_LOGO_URL = `${process.env.PUBLIC_URL || ''}/websitepal_logo.png`;

// How long a signed-in member of staff can sit idle before the till signs them
// out. Enforced in utils/authServiceUtil.js and quoted in the sign-in copy,
// which is why it lives here rather than beside the enforcement — the number
// has to be one value, not two.
//
// Whether it applies at all is the `adminIdleTimeoutEnabled` config flag; with
// that off, the session lasts until a manual sign-out.
export const ADMIN_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Currency symbol printed in front of every amount when a club has not set its
// own (config.currencySymbol). The peso, because that is what every deployment
// predating the currency setting was billing in — see getCurrencySymbol in
// utils/currencyUtil.js.
export const DEFAULT_CURRENCY_SYMBOL = '₱';

// The club's own name for that currency, shown beside the symbol field so
// whoever edits it later knows which currency the symbol refers to.
export const DEFAULT_CURRENCY_DESCRIPTION = 'Philippine Peso';

/**
 * How long any loading state may run before it gives up and shows the
 * connection-error screen.
 *
 * Every loading state in this app waits on a Firebase read or subscription,
 * and neither reports failure on a dead or captive-portal connection — the
 * callback simply never fires. Without this limit the spinner runs forever.
 *
 * Overridable per deployment with REACT_APP_LOADING_TIMEOUT_SECONDS in .env.
 * That name must keep the REACT_APP_ prefix (Create React App only inlines
 * variables named that way into the browser bundle) and is read at BUILD time,
 * so a change needs a dev-server restart or a rebuild to take effect.
 */
export const DEFAULT_LOADING_TIMEOUT_SECONDS = 10;

/**
 * That limit in milliseconds — the single value every loading state is
 * measured against.
 *
 * A missing, blank, non-numeric or zero/negative value falls back to the
 * default rather than being used as-is: setTimeout treats NaN as 0, which
 * would fire the error screen instantly on every load. Any other number is
 * honoured exactly as configured.
 */
export const LOADING_TIMEOUT_MS = (() => {
  const configured = Number(process.env.REACT_APP_LOADING_TIMEOUT_SECONDS);
  const seconds = Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_LOADING_TIMEOUT_SECONDS;
  return seconds * 1000;
})();

// Shared copy for the timeout screen. Every screen that can time out shows
// exactly this — the wording covers both causes (the connection, or the site
// failing to load) without needing a per-screen variant.
export const CONNECTION_ERROR_TITLE = "We're sorry!";
export const CONNECTION_ERROR_MESSAGE = 'It looks like there’s an issue with your internet connection or an issue with loading the site, please try again.';
export const CONNECTION_ERROR_RETRY_LABEL = 'Try Again';

/**
 * Artwork for that screen: the client logo shipped in public/, NOT the logo
 * uploaded through the club's config.
 *
 * Deliberate — this screen only ever renders because the club's config could
 * not be read, so the configured logoUrl (and often its localStorage cache
 * alongside it) is the one thing that cannot be relied on here.
 */
export const CONNECTION_ERROR_LOGO_URL = `${process.env.PUBLIC_URL || ''}/logo.png`;

// The two headings on every checkout card (the subscription checkout here),
// worded exactly as the court-booking system's checkout so a club that uses
// both reads the same instructions in both.
export const PAYMENT_STEP_LABELS = {
  PAY: 'STEP 1: SCAN THE QR CODE TO PAY',
  UPLOAD: 'STEP 2: UPLOAD RECEIPT AND SUBMIT',
};

// ---- Checkout in its own browser tab ----
// The payment step of a subscription opens in a separate tab, the way the
// court-booking system's checkout does: the opening screen hands the order
// over, the checkout tab takes the payment and submits, then reports back and
// closes itself. The two tabs share one "checkout session", kept in
// localStorage and passed over a BroadcastChannel (or localStorage events
// where that API is missing) — see utils/checkoutSessionUtil.js,
// hooks/useCheckoutTab.js and hooks/useCheckoutPageSession.js.

// The checkout tab's routes; the session id is appended as a path segment.
// A till's renewal checkout sits under the client (/<client-id>/checkout/<id>)
// so the client's own config and theme load with it; the landing page's
// sign-up checkout belongs to no client (/checkout/<id>).
export const CHECKOUT_PATH_SEGMENT = 'checkout';
export const CHECKOUT_SIGNUP_ROUTE_PATH = `/${CHECKOUT_PATH_SEGMENT}`;

// What a checkout tab is showing (hooks/useCheckoutPageSession.js).
//   PROBING  - no stored session; asking the opening tab for it
//   WAITING  - the opening tab has not handed the order over yet
//   ACTIVE   - the checkout card
//   ENDED    - checkout finished but the browser refused to close the tab
//   INACTIVE - nothing to show (an old link, or the opening tab never answered)
export const CHECKOUT_PAGE_PHASES = {
  PROBING: 'probing',
  WAITING: 'waiting',
  ACTIVE: 'active',
  ENDED: 'ended',
  INACTIVE: 'inactive',
};

// BroadcastChannel name prefix; each session gets its own channel.
export const CHECKOUT_CHANNEL_NAME = 'websitepal-pos-checkout';

// localStorage key prefix for a session's payload.
export const CHECKOUT_STORAGE_KEY_PREFIX = 'websitepal-pos:checkout:';

// Sessions nobody cleaned up (tab crashed, browser closed) are pruned from
// localStorage after this long.
export const CHECKOUT_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Stored session states. PREPARING is written the moment the tab is opened;
// READY once the order has been handed over.
export const CHECKOUT_SESSION_STATES = {
  PREPARING: 'preparing',
  READY: 'ready',
};

// Messages between the two tabs.
//   opening tab -> checkout tab: SESSION, CLOSE
//   checkout tab -> opening tab: REQUEST_SESSION, SUBMITTED, CANCELLED, EXPIRED
export const CHECKOUT_EVENTS = {
  REQUEST_SESSION: 'request_session',
  SESSION: 'session',
  CLOSE: 'close',
  SUBMITTED: 'submitted',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
};

// Which side sent a message — each tab ignores its own.
export const CHECKOUT_MESSAGE_SOURCES = {
  OPENER: 'opener',
  CHECKOUT: 'checkout',
};

// What the opening tab knows about the checkout tab.
//   IDLE      - no checkout in progress
//   PREPARING - tab opened, order not handed over yet
//   OPEN      - tab open with the order loaded
//   BLOCKED   - the browser refused to open the tab (popup blocker)
//   CLOSED    - the tab was closed without finishing
export const CHECKOUT_TAB_STATES = {
  IDLE: 'idle',
  PREPARING: 'preparing',
  OPEN: 'open',
  BLOCKED: 'blocked',
  CLOSED: 'closed',
};

// How often the opening tab checks whether the checkout tab was closed.
export const CHECKOUT_TAB_POLL_MS = 1000;

// How long a freshly opened checkout tab waits for the order to be handed over.
export const CHECKOUT_PREPARE_TIMEOUT_MS = 60 * 1000;

// How long a checkout tab with no stored session waits for the opening tab
// to answer before showing "no longer active".
export const CHECKOUT_PROBE_TIMEOUT_MS = 3000;

// Short pause before the checkout tab closes itself, so its last message is
// delivered first.
export const CHECKOUT_CLOSE_DELAY_MS = 150;

// kawnter-be email routes, appended to REACT_APP_KAWNTER_BE_URL (which ends
// in /api). See src/utils/emailServiceUtil.js.
export const KAWNTER_BE_EMAIL_ROUTES = {
  SIGNUP_RECEIVED: '/emails/signup-received',
  SIGNUP_COMPLETED: '/emails/signup-completed',
  APPLICATION_APPROVED: '/emails/application-approved',
};
